import React, { useEffect, useMemo, useRef, useState } from "react";
import InputDropdown from "../../../../../components/inputs/inputDropdown/inputDropdown";
import Button from "../../../../../components/button/button";
import Notification from "../../../../../components/notification/notification";
import {
  HiOutlineHome,
  HiOutlineCube,
  HiOutlineColorSwatch,
  HiOutlineLightBulb,
  HiOutlineVolumeUp,
  HiOutlineSparkles,
  HiOutlineTrash
} from "react-icons/hi";
import { BiExport } from "react-icons/bi";
import { getApiInstance } from "../../../../../utils/api";
import "./comparativa.scss";

const CATEGORIAS_CONFIG = [
  { key: "mobiliarioGeneral", label: "Mobiliario General", categoriaReal: "Mobiliario", icono: <HiOutlineHome /> },
  { key: "mobiliarioEspecial", label: "Mobiliario Especial", categoriaReal: "Mobiliario especial", icono: <HiOutlineCube /> },
  { key: "revestimientosMobiliario", label: "Revestimientos Mobiliario", categoriaReal: "Revestimientos mobiliario", icono: <HiOutlineSparkles /> },
  { key: "suelosMobiliario", label: "Suelos Mobiliario", categoriaReal: "Suelo Tarkett", icono: <HiOutlineColorSwatch /> },
  { key: "audiovisuales", label: "Audiovisuales (Audio + Pantallas)", categoriaReal: "Audiovisuales", icono: <HiOutlineVolumeUp /> },
  { key: "iluminacion", label: "Iluminación", categoriaReal: "Iluminacion", icono: <HiOutlineLightBulb /> }
];

const VERSION_COMPARAR_NINGUNO = {
  label: "NINGUNO",
  value: "__NINGUNO__",
  isNone: true
};

const Comparativa = () => {
  const api = getApiInstance();
  const notificationRef = useRef();

  const [loading, setLoading] = useState(false);
  const [versiones, setVersiones] = useState([]);
  const [versionBase, setVersionBase] = useState(null);
  const [versionComparar, setVersionComparar] = useState(null);
  const [versionExportar, setVersionExportar] = useState(null);
  const [detalleBase, setDetalleBase] = useState([]);
  const [detalleComparar, setDetalleComparar] = useState([]);

  useEffect(() => {
    cargarVersiones();
  }, []);

  useEffect(() => {
    if (versionBase?.value) cargarDetalleVersion(versionBase.value, setDetalleBase);
  }, [versionBase]);

  useEffect(() => {
    if (versionComparar?.isNone) {
      setDetalleComparar([]);
      return;
    }

    if (versionComparar?.value) cargarDetalleVersion(versionComparar.value, setDetalleComparar);
  }, [versionComparar]);

  const cargarVersiones = async () => {
    try {
      setLoading(true);
      console.log('Llamando API para obtener todas las versiones');

      const response = await api.get('/api/PresupuestosVersiones');
      const data = response.data || [];
      console.log('Respuesta API versiones:', data);

      const options = data.map((item) => ({
        label: construirLabelVersion(item),
        value: item.codVersionPresupuesto,
        raw: item,
        // Campos adicionales para facilitar la búsqueda
        searchableText: `${item.codigoTienda || ''} ${item.nombreVersion || ''} ${item.cadena || ''} ${item.ciudad || ''} ${item.pais || ''}`.toLowerCase()
      }));

      setVersiones(options);
      console.log('Versiones cargadas:', options);
    } catch (error) {
      console.error('Error en cargarVersiones:', error);
      notificationRef.current?.addNotification("Error al cargar las versiones", "error");
    } finally {
      setLoading(false);
    }
  };

  const cargarDetalleVersion = async (codVersionPresupuesto, setter) => {
    try {
      console.log('Llamando API cargarDetalleVersion con codVersionPresupuesto:', codVersionPresupuesto);
      const response = await api.get(`/api/PresupuestosVersiones/${codVersionPresupuesto}/detalle`);
      console.log('Respuesta API detalle:', response.data);
      setter(response.data || []);
    } catch (error) {
      console.error('Error en cargarDetalleVersion:', error);
      setter([]);
      notificationRef.current?.addNotification("Error al cargar el detalle de la versión", "error");
    }
  };

  const construirLabelVersion = (item) => {
    const nombre = item.nombreVersion || `Versión ${item.codVersionPresupuesto}`;
    const codigoTienda = item.codigoTienda || 'Sin código';
    const fecha = item.fechaGuardado
      ? new Date(item.fechaGuardado).toLocaleString("es-ES", {
          day: '2-digit',
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : "";

    // Formato: [CÓDIGO_TIENDA] Nombre de la versión - Fecha
    return fecha 
      ? `[${codigoTienda}] ${nombre} - ${fecha}` 
      : `[${codigoTienda}] ${nombre}`;
  };

  const sumarPorCategoria = (detalle, categoriaReal) => {
    return (detalle || [])
      .filter(
        (x) => (x.categoriaCatalogo || "").trim().toLowerCase() === categoriaReal.trim().toLowerCase()
      )
      .reduce((sum, item) => sum + (Number(item.precioTotal) || 0), 0);
  };

  const filasComparativa = useMemo(() => {
    return CATEGORIAS_CONFIG.map((categoria) => {
      const base = sumarPorCategoria(detalleBase, categoria.categoriaReal);
      const comparar = sumarPorCategoria(detalleComparar, categoria.categoriaReal);
      const diferencia = base - comparar;
      const porcentaje = comparar !== 0 ? (diferencia / comparar) * 100 : 0;

      return {
        ...categoria,
        base,
        comparar,
        diferencia,
        porcentaje
      };
    });
  }, [detalleBase, detalleComparar]);

  const opcionesVersionComparar = useMemo(
    () => [VERSION_COMPARAR_NINGUNO, ...versiones],
    [versiones]
  );

  const resumen = useMemo(() => {
    const totalBase = filasComparativa.reduce((sum, item) => sum + item.base, 0);
    const totalComparar = filasComparativa.reduce((sum, item) => sum + item.comparar, 0);
    const desviacion = totalBase - totalComparar;

    return {
      totalBase,
      totalComparar,
      desviacion,
      porcentajeBase: totalComparar > 0 ? (totalBase / totalComparar) * 100 : null,
      porcentajeComparar: totalComparar > 0 ? 100 : null,
      porcentajeDesviacion: totalComparar > 0 ? (desviacion / totalComparar) * 100 : null
    };
  }, [filasComparativa]);

  const formatearNumero = (numero) =>
    Number(numero || 0).toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const formatearPorcentaje = (valor) => {
    if (valor === null || valor === undefined) return "-";
    return `${Number(valor).toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}%`;
  };

  const getClaseDiferencia = (valor) => {
    if (valor > 0) return "positive";
    if (valor < 0) return "negative";
    return "neutral";
  };

  const handleExport = async () => {
    if (!versionExportar?.value) {
      notificationRef.current?.addNotification("Selecciona una versión para exportar", "warning");
      return;
    }

    try {
      console.log('Exportando versión:', versionExportar.value);
      const response = await api.get(
        `/api/PresupuestosVersiones/${versionExportar.value}/exportar-excel`,
        { responseType: "blob" }
      );

      console.log('Respuesta exportación:', response);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let filename = "presupuesto_version.xlsx";
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notificationRef.current?.addNotification("Versión exportada correctamente", "success");
    } catch (error) {
      console.error('Error en exportación:', error);
      notificationRef.current?.addNotification("Error al exportar la versión", "error");
    }
  };

  const limpiarDatos = () => {
    setVersionBase(null);
    setVersionComparar(null);
    setVersionExportar(null);
    setDetalleBase([]);
    setDetalleComparar([]);
  };

  return (
    <div className="comparativa-versiones">
      {/* Área de controles de exportación */}
      <div className="comparativa-export-area">
        <div className="export-controls">
          <div className="export-selector">
            <label className="export-label">Selecciona versión a exportar:</label>
            <InputDropdown
              options={versiones}
              onSelect={setVersionExportar}
              value={versionExportar}
              placeholder="Selecciona versión a exportar"
              hasSearch={false}
              showLabel={false}
            />
          </div>
          <Button
            variant="filled"
            icon={<BiExport />}
            label="Exportar"
            onClick={handleExport}
            disabled={!versionExportar?.value || loading}
          />
          <Button
            variant="outline"
            icon={<HiOutlineTrash />}
            label="Borrar datos"
            onClick={limpiarDatos}
            disabled={loading}
          />
        </div>
      </div>

      {/* Área de selección de versiones para comparar */}
      <div className="comparativa-columns-layout">
        <div className="column-section column-base">
          <div className="column-header">Versión Base</div>
          <div className="column-content">
            <InputDropdown
              options={versiones}
              onSelect={setVersionBase}
              value={versionBase}
              placeholder="Buscar por código/nombre de tienda..."
              hasSearch={true}
              showLabel={false}
            />
          </div>
        </div>

        <div className="column-section column-comparar">
          <div className="column-header">Versión Comparar</div>
          <div className="column-content">
            <InputDropdown
              options={opcionesVersionComparar}
              onSelect={setVersionComparar}
              value={versionComparar}
              placeholder="Buscar por código/nombre de tienda..."
              hasSearch={true}
              showLabel={false}
            />
          </div>
        </div>

        <div className="column-section column-diferencia">
          <div className="column-header">Desviación</div>
          <div className="column-content">
            <div className="desviacion-value">
              <span className={getClaseDiferencia(resumen.desviacion)}>
                {formatearNumero(resumen.desviacion)}€
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Área de resumen alineada con las columnas */}
      <div className="comparativa-summary-aligned">
        <div className="summary-section summary-base">
          <div className="summary-header">Base</div>
          <div className="summary-content">
            <div className="summary-row">
              <span className="summary-label">Total:</span>
              <span className="summary-value">{formatearNumero(resumen.totalBase)}€</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Porcentaje:</span>
              <span className="summary-value">{formatearPorcentaje(resumen.porcentajeBase)}</span>
            </div>
          </div>
        </div>

        <div className="summary-section summary-comparar">
          <div className="summary-header">Comparar</div>
          <div className="summary-content">
            <div className="summary-row">
              <span className="summary-label">Total:</span>
              <span className="summary-value">{formatearNumero(resumen.totalComparar)}€</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Porcentaje:</span>
              <span className="summary-value">{formatearPorcentaje(resumen.porcentajeComparar)}</span>
            </div>
          </div>
        </div>

        <div className="summary-section summary-diferencia">
          <div className="summary-header">Diferencia</div>
          <div className="summary-content">
            <div className="summary-row">
              <span className="summary-label">Total:</span>
              <span className={`summary-value ${getClaseDiferencia(resumen.desviacion)}`}>
                {formatearNumero(resumen.desviacion)}€
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Porcentaje:</span>
              <span className={`summary-value ${getClaseDiferencia(resumen.desviacion)}`}>
                {formatearPorcentaje(resumen.porcentajeDesviacion)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="comparativa-versiones__tabla">
        <div className="tabla-header">
          <div className="columna categoria">Categoría</div>
          <div className="columna">Base</div>
          <div className="columna">Comparar</div>
          <div className="columna">Diferencia</div>
          <div className="columna">% Variación</div>
        </div>

        {filasComparativa.map((fila) => (
          <div key={fila.key} className="tabla-fila">
            <div className="columna categoria">
              {fila.icono}
              <span>{fila.label}</span>
            </div>
            <div className="columna">{formatearNumero(fila.base)}€</div>
            <div className="columna">{formatearNumero(fila.comparar)}€</div>
            <div className={`columna ${getClaseDiferencia(fila.diferencia)}`}>
              {fila.diferencia > 0 ? "+" : ""}
              {formatearNumero(fila.diferencia)}€
            </div>
            <div className={`columna ${getClaseDiferencia(fila.diferencia)}`}>
              {formatearPorcentaje(fila.porcentaje)}
            </div>
          </div>
        ))}
      </div>

      <Notification ref={notificationRef} />
    </div>
  );
};

export default Comparativa;
